import {
  Siren,
  SignOut,
  SpeakerHigh,
  SpeakerSlash,
  CaretDown,
  UserCircle,
  Gear,
  PencilSimple,
  UsersThree,
  Diamond,
  Trophy,
  Gift,
  Crown,
  Plus,
  X,
  Wrench,
  DiscordLogo,
  ArrowsOut,
  Monitor,
  Question,
  Newspaper,
  Lightning,
} from "@phosphor-icons/react";
import { useFullscreen } from "@/hooks/useFullscreen";
import PremiumBadge from "@/components/PremiumBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function HUD({
  player,
  activeCalls,
  onLogout,
  onChangeUsername,
  onOpenProfile,
  onOpenSettings,
  onOpenFaqHelp,
  onOpenFriends,
  friendNotificationCount = 0,
  friendIncomingRequests = 0,
  friendUnreadMessages = 0,
  onOpenDeveloperPanel,
  isDeveloper = false,
  onOpenNews,
  newsUnreadCount = 0,
  gameVersion,
  onOpenMutualAid,
  onOpenBuyGems,
  onOpenBuyPremium,
  onOpenAchievements,
  onOpenDailyBonus,
  dailyClaimable,
  hasAlliance,
  isPremium,
  placeMode,
  placeModePayWith,
  placeHospitalMode,
  placeHospitalPayWith,
  onTogglePlace,
  onTogglePlaceGems,
  onToggleBuildHospital,
  onTogglePlacePolice,
  onTogglePlacePoliceGems,
  placePoliceMode,
  placePolicePayWith,
  stationsCount,
  policeStationsCount,
  nextStationCost,
  nextStationGemCost,
  nextPoliceStationCost,
  nextPoliceStationGemCost,
  activeEvents = [],
  hospitalCost,
  hospitalGemCost,
  soundOn,
  onToggleSound,
}) {
  return (
    <>
    <header className="absolute top-0 left-0 right-0 h-[60px] border-b border-white/10 flex items-center px-2 sm:px-4 bg-[#050505] z-50">
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="w-8 h-8 sm:w-9 sm:h-9 border border-red-500/60 flex items-center justify-center bg-red-500/10">
          <Siren size={18} weight="bold" color="#FF3B30" />
        </div>
        <div className="leading-tight hidden sm:block">
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">DISPATCH-911</div>
          <div className="text-sm font-bold tracking-tight flex items-center gap-1.5" data-testid="hud-username">
            {isPremium && <PremiumBadge size={13} />}
            {player.username.toUpperCase()}
          </div>
        </div>
      </div>

      <div className="ml-3 sm:ml-8 flex items-center gap-3 sm:gap-6 overflow-hidden">
        <Stat label="MONEY" value={`$${player.money.toLocaleString()}`} valueClass="text-emerald-400" testid="hud-money" />
        <Stat
          label="GEMS"
          value={
            <span className="inline-flex items-center gap-1.5">
              <Diamond size={14} weight="fill" className="text-cyan-300" />
              {(player.gems ?? 0).toLocaleString()}
            </span>
          }
          valueClass="text-cyan-300"
          testid="hud-gems"
        />
        <span className="hidden md:contents">
          <Stat label="STATIONS" value={stationsCount} valueClass="text-white" testid="hud-stations" />
          <Stat
            label="ACTIVE"
            value={activeCalls}
            valueClass={activeCalls > 0 ? "text-yellow-400" : "text-zinc-500"}
            testid="hud-active"
          />
        </span>
      </div>

      <div className="ml-auto flex items-center gap-1 lg:gap-2 shrink-0">
        <Button
          data-testid="daily-bonus-btn"
          onClick={onOpenDailyBonus}
          variant="ghost"
          title={dailyClaimable ? "Claim your daily gem bonus!" : "Daily bonus already claimed"}
          className={`relative rounded-none h-9 w-9 sm:w-auto p-0 sm:px-3 mono uppercase text-[11px] tracking-widest hover:bg-white/5 ${
            dailyClaimable ? "text-cyan-300" : "text-zinc-500"
          }`}
        >
          <Gift size={14} weight={dailyClaimable ? "fill" : "regular"} />
          {dailyClaimable && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-cyan-400 animate-pulse" />
          )}
        </Button>
        <Button
          data-testid="sound-toggle-btn"
          onClick={onToggleSound}
          variant="ghost"
          title={soundOn ? "Sound on (click to mute)" : "Sound off (click to enable)"}
          className={`hidden sm:inline-flex rounded-none h-9 mono uppercase text-[11px] tracking-widest hover:bg-white/5 ${
            soundOn ? "text-emerald-400" : "text-zinc-500"
          }`}
        >
          {soundOn ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />}
        </Button>
        {placeMode || placeHospitalMode || placePoliceMode ? (
          <Button
            data-testid="cancel-placement-btn"
            onClick={placeHospitalMode
              ? () => onToggleBuildHospital && onToggleBuildHospital(placeHospitalPayWith || "money")
              : placePoliceMode
                ? () => (placePolicePayWith === "gems"
                    ? onTogglePlacePoliceGems && onTogglePlacePoliceGems()
                    : onTogglePlacePolice && onTogglePlacePolice())
                : onTogglePlace}
            className="rounded-none h-9 mono uppercase text-[11px] tracking-widest border bg-emerald-500 text-black hover:bg-emerald-400 border-emerald-400"
          >
            <X size={12} className="mr-1.5" />
            Cancel Placement
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="build-facility-btn"
                className="rounded-none h-9 px-2 lg:px-3 mono uppercase text-[11px] tracking-widest border bg-transparent text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"
                title="Build Facility"
              >
                <Plus size={12} className="lg:mr-1.5" />
                <span className="hidden lg:inline">Build Facility</span>
                <CaretDown size={11} className="ml-1 lg:ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-950 border border-white/10 rounded-none w-80 p-0"
              data-testid="build-facility-menu"
            >
              <DropdownMenuLabel className="mono text-[9px] uppercase tracking-widest text-zinc-500 px-3 pt-2 pb-1">
                Choose Facility
              </DropdownMenuLabel>

              {/* Fire Station row */}
              <div className="px-3 py-2 border-t border-white/5" data-testid="build-row-station">
                <div className="mono text-[10px] uppercase tracking-widest text-white mb-1.5">Fire Station</div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid="build-station-cash"
                    onClick={onTogglePlace}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  >
                    ${(nextStationCost ?? 20000).toLocaleString()}
                  </button>
                  <button
                    type="button"
                    data-testid="build-station-gems"
                    onClick={onTogglePlaceGems}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center justify-center gap-1"
                  >
                    <Diamond size={10} weight="fill" /> {(nextStationGemCost ?? 0).toLocaleString()}
                  </button>
                </div>
              </div>

              {/* Police Station row */}
              <div className="px-3 py-2 border-t border-white/5" data-testid="build-row-police-station">
                <div className="mono text-[10px] uppercase tracking-widest text-blue-300 mb-1.5">Police Station</div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid="build-police-station-cash"
                    onClick={onTogglePlacePolice}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                  >
                    ${(nextPoliceStationCost ?? 500_000).toLocaleString()}
                  </button>
                  <button
                    type="button"
                    data-testid="build-police-station-gems"
                    onClick={onTogglePlacePoliceGems}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center justify-center gap-1"
                  >
                    <Diamond size={10} weight="fill" /> {(nextPoliceStationGemCost ?? 1000).toLocaleString()}
                  </button>
                </div>
              </div>

              {/* Hospital row */}
              <div className="px-3 py-2 border-t border-white/5" data-testid="build-row-hospital">
                <div className="mono text-[10px] uppercase tracking-widest text-pink-300 mb-1.5">Hospital</div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid="build-hospital-cash"
                    onClick={() => onToggleBuildHospital && onToggleBuildHospital("money")}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-pink-500/40 text-pink-300 hover:bg-pink-500/10"
                  >
                    ${(hospitalCost ?? 1_000_000).toLocaleString()}
                  </button>
                  <button
                    type="button"
                    data-testid="build-hospital-gems"
                    onClick={() => onToggleBuildHospital && onToggleBuildHospital("gems")}
                    className="flex-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center justify-center gap-1"
                  >
                    <Diamond size={10} weight="fill" /> {(hospitalGemCost ?? 2000).toLocaleString()}
                  </button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!isPremium && (
          <Button
            data-testid="buy-premium-btn"
            onClick={onOpenBuyPremium}
            title="Unlock Premium (gold helmet + faster spawn speeds)"
            className="rounded-none h-9 px-2 lg:px-3 mono uppercase text-[11px] tracking-widest border bg-transparent text-amber-300 border-amber-400/50 hover:bg-amber-400/10"
          >
            <Crown size={13} weight="fill" className="lg:mr-1.5 text-amber-400" />
            <span className="hidden lg:inline">Buy Premium</span>
          </Button>
        )}

        <Button
          data-testid="buy-gems-btn"
          onClick={onOpenBuyGems}
          title="Buy Gems with credit card or PayPal"
          className="rounded-none h-9 px-2 lg:px-3 mono uppercase text-[11px] tracking-widest border bg-transparent text-cyan-300 border-cyan-400/40 hover:bg-cyan-400/10"
        >
          <Diamond size={14} weight="fill" className="lg:mr-1.5 text-cyan-300" />
          <span className="hidden lg:inline">Buy Gems!</span>
        </Button>

        <Button
          data-testid="mutual-aid-btn"
          onClick={onOpenMutualAid}
          variant="ghost"
          title="Mutual Aid Groups"
          className={`hidden lg:inline-flex rounded-none h-9 mono uppercase text-[11px] tracking-widest border ${
            hasAlliance
              ? "bg-transparent text-orange-300 border-orange-500/40 hover:bg-orange-500/10"
              : "bg-transparent text-zinc-300 border-white/15 hover:bg-white/5"
          }`}
        >
          <UsersThree size={14} className="mr-1.5" />
          Mutual Aid
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="user-menu-trigger"
              variant="ghost"
              title={player.username}
              className="relative rounded-none h-9 px-2 lg:px-3 mono uppercase text-[11px] tracking-widest text-zinc-300 hover:text-white hover:bg-white/5 border border-white/10"
            >
              <UserCircle size={14} className="lg:mr-1.5" />
              <span className="hidden lg:inline">{player.username}</span>
              <CaretDown size={12} className="ml-1 lg:ml-1.5 text-zinc-500" />
              {friendNotificationCount > 0 && (
                <span
                  data-testid="hud-friend-notification-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] mono font-bold border border-[#0a0a0a] rounded-full shadow-md shadow-red-500/50 animate-pulse"
                  title={`${friendIncomingRequests} request(s), ${friendUnreadMessages} unread message(s)`}
                >
                  {friendNotificationCount > 9 ? "9+" : friendNotificationCount}
                </span>
              )}
              {/* Sky-blue dot when unread news exists. Placed at bottom-right so
                  it doesn't collide with the red friend-notification badge at
                  top-right. Purely a hint — the actual count lives on the
                  "News" menu row inside the dropdown. */}
              {newsUnreadCount > 0 && (
                <span
                  data-testid="hud-news-notification-dot"
                  aria-label={`${newsUnreadCount} unread news post${newsUnreadCount === 1 ? "" : "s"}`}
                  className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-sky-400 border border-[#0a0a0a] rounded-full shadow-md shadow-sky-400/50"
                  title={`${newsUnreadCount} new news post${newsUnreadCount === 1 ? "" : "s"}`}
                />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="user-menu-content"
            align="end"
            className="rounded-none border border-white/10 bg-[#0F1115] text-zinc-200 min-w-[200px] p-0"
          >
            <DropdownMenuLabel className="mono text-[9px] uppercase tracking-widest text-zinc-500 px-3 py-2">
              Signed in as {player.username}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem
              data-testid="menu-profile"
              onClick={onOpenProfile}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3"
            >
              <UserCircle size={13} className="mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-achievements"
              onClick={onOpenAchievements}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3"
            >
              <Trophy size={13} className="mr-2" /> Achievements
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-friends"
              onClick={onOpenFriends}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3 flex items-center"
            >
              <UsersThree size={13} className="mr-2" weight="fill" /> Friends
              {friendNotificationCount > 0 && (
                <span
                  data-testid="menu-friends-badge"
                  className="ml-auto bg-red-500 text-white text-[9px] mono font-bold min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full"
                >
                  {friendNotificationCount > 9 ? "9+" : friendNotificationCount}
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-settings"
              onClick={onOpenSettings}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3"
            >
              <Gear size={13} className="mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-faq-help"
              onClick={onOpenFaqHelp}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3"
            >
              <Question size={13} className="mr-2" weight="fill" /> FAQ / Help
            </DropdownMenuItem>
            {/* Fullscreen toggle — hidden when the browser doesn't support
                the API (rare) or we're in a standalone PWA shell (already
                fullscreen). */}
            <FullscreenMenuRow />
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem
              data-testid="menu-discord"
              onClick={(e) => {
                // Prevent the dropdown from intercepting the navigation
                // and open in a new tab so the active game session stays alive.
                e.preventDefault();
                window.open("https://discord.gg/vvAE4PTvU9", "_blank", "noopener,noreferrer");
              }}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-[#5865F2]/15 focus:text-[#8d97ff] text-[#8d97ff] py-2 px-3"
              style={{ color: "#8d97ff" }}
            >
              <DiscordLogo size={13} weight="fill" className="mr-2" style={{ color: "#5865F2" }} />
              Join Discord
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-news"
              onClick={onOpenNews}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-sky-500/10 focus:text-sky-200 text-sky-300 py-2 px-3 relative"
            >
              <Newspaper size={13} className="mr-2" weight="fill" /> News
              {newsUnreadCount > 0 && (
                <span
                  data-testid="menu-news-badge"
                  aria-label={`${newsUnreadCount} unread news post${newsUnreadCount === 1 ? "" : "s"}`}
                  className="ml-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold tracking-normal"
                >
                  {newsUnreadCount > 9 ? "9+" : newsUnreadCount}
                </span>
              )}
            </DropdownMenuItem>
            {isDeveloper && (
              <DropdownMenuItem
                data-testid="menu-developer-panel"
                onClick={onOpenDeveloperPanel}
                className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-amber-500/10 focus:text-amber-200 text-amber-300 py-2 px-3"
              >
                <Wrench size={13} className="mr-2" weight="fill" /> Developer Panel
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem
              data-testid="menu-logout"
              onClick={onLogout}
              className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-red-500/10 focus:text-red-300 text-red-400 py-2 px-3"
            >
              <SignOut size={13} className="mr-2" /> Logout
            </DropdownMenuItem>
            {gameVersion && (
              <div
                data-testid="menu-game-version"
                className="mono text-[9px] uppercase tracking-widest text-zinc-600 py-1.5 px-3 border-t border-white/5 select-none"
              >
                Game Version · {gameVersion}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
    {/* Event banner — appears just below the top HUD when at least one
        event is active. Shows the event name + which credit multipliers
        are on. When multiple events overlap, the first one's name is
        used and the boosts are unioned. */}
    {activeEvents && activeEvents.length > 0 && (
      <div
        data-testid="event-banner"
        className="absolute top-[60px] left-0 right-0 z-40 border-b border-amber-400/40 bg-gradient-to-r from-amber-500/[0.14] via-amber-500/[0.08] to-transparent px-3 py-1 flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-amber-200 pointer-events-none"
      >
        <Lightning size={12} weight="fill" className="text-amber-300 animate-pulse" />
        <span data-testid="event-banner-name" className="font-bold text-amber-100">
          LIVE · {activeEvents[0].name}
        </span>
        {activeEvents.some((e) => e.double_mission_credits) && (
          <span data-testid="event-banner-mission" className="text-emerald-300 border border-emerald-400/40 px-1.5 py-px">
            2× MISSIONS
          </span>
        )}
        {activeEvents.some((e) => e.double_purchase_credits) && (
          <span data-testid="event-banner-purchase" className="text-cyan-300 border border-cyan-400/40 px-1.5 py-px">
            2× GEMS ON PURCHASE
          </span>
        )}
        {activeEvents.length > 1 && (
          <span className="text-zinc-400">+{activeEvents.length - 1} more</span>
        )}
      </div>
    )}
    </>
  );
}

function Stat({ label, value, valueClass, testid }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{label}</span>
      <span className={`mono font-bold text-base ${valueClass}`} data-testid={testid}>
        {value}
      </span>
    </div>
  );
}

// ---- Fullscreen toggle row used inside the user dropdown ----
// Kept here so it can call useFullscreen at the right scope (DropdownMenu
// children don't get re-rendered while the menu is closed, which would
// otherwise miss fullscreenchange events). Renders nothing if the browser
// can't go fullscreen or the app is already in a PWA standalone shell.
function FullscreenMenuRow() {
  const { supported, isFullscreen, isStandalonePWA, toggle } = useFullscreen();
  if (!supported || isStandalonePWA) return null;
  return (
    <DropdownMenuItem
      data-testid="menu-fullscreen"
      onClick={(e) => {
        // Don't let the dropdown close before the fullscreen request
        // fires — Safari requires the call to happen synchronously from
        // a user gesture, and Radix's close animation breaks that chain.
        e.preventDefault();
        toggle();
      }}
      className="mono text-[11px] uppercase tracking-widest cursor-pointer rounded-none focus:bg-white/5 focus:text-white py-2 px-3"
    >
      {isFullscreen ? (
        <><Monitor size={13} className="mr-2" /> Exit Fullscreen</>
      ) : (
        <><ArrowsOut size={13} className="mr-2" /> Enter Fullscreen</>
      )}
    </DropdownMenuItem>
  );
}
